import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  MultiSelect,
  NumberInput,
  Select,
  Switch,
  TagsInput,
  Text,
  TextInput,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { z } from 'zod';
import { TEXT_DIRECTIONS } from '@seo/contracts';
import { publicMessageOf, useTRPC } from '../lib/trpc';
import { zodResolver } from '../lib/zod-resolver';
import { RowEditor } from '../components/RowEditor';
import classes from './EntityPage.module.css';

/**
 * Картка ГЕО-пресету — те, що мокап малює як `geoNew` (4 секції, ~27 полів) — і водночас
 * форма створення. Одна форма, а не дві: реєстр заводив пресет сімкою полів у `CreatePanel`,
 * а решта ~20 (валютний формат, ліміти виводу, KYC, дисклеймер, обмеження бонусів, заборонені
 * механіки) існувала лише тут, у картці редагування, що відкривалась окремим кліком — оператор
 * мусив уже знати, що другий екран існує, щоб довести пресет до стану, придатного для генерації.
 *
 * `row` відсутній ⇒ створення нового пресету; `row` присутній ⇒ редагування наявного. Той самий
 * набір полів і той самий підказковий стан «обмеження на бонуси» (нижче) працює для обох —
 * дефект «поле є в редагуванні, нема при створенні» так більше не заводиться.
 *
 * Тут — юридичний шар, який і робить пресет пресетом: вік, KYC, дисклеймер, обмеження бонусів,
 * заборонені механіки й перемикач, що ховає бонус-блоки в усій мережі. `countryCode`
 * редагується лише при створенні: це ключ, за яким на пресет посилаються сайти й ключові
 * слова, і його зміна після створення означає, що ГЕО стало іншою країною з тими самими
 * сайтами — таке роблять новим пресетом і перенесенням, а не редагуванням поля.
 */
export interface EditableGeoPreset {
  id: string;
  countryCode: string;
  countryName: string;
  languages: string[];
  primaryLanguage: string;
  minAge: number;
  hideBonusBlocks: boolean;
  /**
   * Решта — необовʼязкові в типі, а не «завжди присутні»: `getGeoPresetById` завжди віддає
   * повний рядок з колонковими дефолтами (`textDirection: 'ltr'`, `timezone: 'UTC'`, …), але
   * робити їх обовʼязковими тут ламало б будь-який мінімальний фікстур на кшталт того, що вже
   * існував (і існує в `RegistryEditors.test.tsx`) до цих 19 полів. Дефолти нижче — ті самі
   * значення, що стоять у `packages/db/src/schema/geo.ts`.
   */
  textDirection?: 'ltr' | 'rtl';
  timezone?: string;
  phoneCode?: string | null;
  dateFormat?: string;
  numberFormat?: string;
  currencyCode: string;
  currencyFormat?: string;
  minWithdrawal?: number | null;
  maxWithdrawal?: number | null;
  payoutTime?: string | null;
  regulatorId?: string | null;
  allowedFooterLicenses?: string[];
  kycRequirements?: string | null;
  mandatoryDisclaimer?: string | null;
  helplineRef?: string | null;
  bonusRestrictions?: string | null;
  forbiddenMechanics?: string[];
}

/** Проста сума в мажорних одиницях, за тим самим правилом, що бонуси на картці бренду. */
const optionalAmountField = (message: string) =>
  z.string().refine((raw) => {
    const value = raw.trim();
    if (value === '') return true;
    return /^\d+(\.\d{1,2})?$/.test(value);
  }, message);

const directionOptions = TEXT_DIRECTIONS.map((value) => ({ value, label: value.toUpperCase() }));

/**
 * Три слоти мови, а не одне поле через кому: мокап малює саме так, і третій слот навмисно
 * порожній — це не забута мова, а нормальний стан пресету з двома мовами. Валідація вимагає
 * лише слот 1; слоти 2 і 3 можуть лишатися «—» скільки завгодно.
 */
const langSlotSchema = z
  .string()
  .max(3)
  .regex(/^([A-Za-z]{2,3})?$/u, 'ISO-код мови, напр. fr, або порожньо');

const geoPresetFormSchema = z
  .object({
    // Валідується завжди, хоча поле редагується лише при створенні (input заблоковано на
    // редагуванні) — той самий регекс, що був у прибраного окремого `CreatePanel`.
    countryCode: z
      .string()
      .length(2, 'Код країни — 2 літери, напр. FR')
      .regex(/^[A-Za-z]{2}$/u, 'Лише літери'),
    countryName: z.string().trim().min(1, 'Вкажіть назву країни').max(200, 'Максимум 200 символів'),
    lang1: z.string().min(2, 'Мова 1 обовʼязкова').max(3).regex(/^[A-Za-z]{2,3}$/u, 'Лише літери'),
    lang2: langSlotSchema,
    lang3: langSlotSchema,
    primaryLanguage: z
      .string()
      .min(2, 'ISO-код мови, напр. fr')
      .max(3)
      .regex(/^[A-Za-z]{2,3}$/u, 'Лише літери'),
    textDirection: z.enum(TEXT_DIRECTIONS),
    timezone: z.string().trim().min(1, 'Вкажіть часовий пояс').max(60),
    phoneCode: z.string().trim().max(10),
    dateFormat: z.string().trim().min(1, 'Вкажіть формат дати').max(30),
    numberFormat: z.string().trim().min(1, 'Вкажіть формат чисел').max(30),
    currencyCode: z
      .string()
      .length(3, 'Валюта — 3 літери, напр. EUR')
      .regex(/^[A-Za-z]{3}$/u, 'Лише літери'),
    currencyFormat: z.string().trim().min(1, 'Вкажіть формат суми').max(40),
    minWithdrawal: optionalAmountField('Сума — число, напр. 20 або 20.50'),
    maxWithdrawal: optionalAmountField('Сума — число, напр. 5000'),
    payoutTime: z.string().trim().max(120),
    regulatorId: z.string(),
    footerLicenses: z.array(z.string()),
    minAge: z.number().int().min(0, 'Ціле число ≥ 0').max(99),
    kycRequirements: z.string().trim().max(2000),
    mandatoryDisclaimer: z.string().trim().max(2000),
    helplineRef: z.string(),
    bonusRestrictions: z.string().trim().max(2000),
    forbiddenMechanics: z.array(z.string().max(60)),
    hideBonusBlocks: z.boolean(),
  })
  .superRefine((values, ctx) => {
    const languages = [values.lang1, values.lang2, values.lang3]
      .map((l) => l.trim().toLowerCase())
      .filter(Boolean);
    if (!languages.includes(values.primaryLanguage.trim().toLowerCase())) {
      ctx.addIssue({
        code: 'custom',
        path: ['primaryLanguage'],
        message: 'Основна мова має бути серед мов 1–3.',
      });
    }
    // «Обмеження на бонуси» навмисно НЕ блокує сабміт тут: `updateGeoPreset` вже відмовляє
    // порожнім полем за увімкненого `hideBonusBlocks`/`forbiddenMechanics`, і дублювати це
    // правило на клієнті означає два місця, які треба міняти синхронно. Нижче — лише
    // непримусова підказка того самого стану, який мокап малює кольором помилки.
  });
type GeoPresetFormValues = z.infer<typeof geoPresetFormSchema>;

export function GeoPresetEditor({
  row,
  duplicateOf,
  onClose,
}: {
  /** Відсутній ⇒ форма створення нового пресету, а не редагування наявного. */
  row?: EditableGeoPreset;
  /**
   * Пресет-зразок для дублювання: форма лишається **створенням**, але поля вже заповнені.
   *
   * Ринки-сусіди відрізняються двома полями з двадцяти семи, і переписувати решту руками —
   * те, від чого кнопка «Дублювати пресет» у мокапі й рятує. Код країни при цьому свідомо
   * порожній: він унікальний, і залишений від зразка дав би відмову на унікальному індексі
   * замість підказки, що саме треба змінити.
   */
  duplicateOf?: EditableGeoPreset;
  onClose: () => void;
}): React.JSX.Element {
  const source = row ?? duplicateOf;
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const isCreate = row === undefined;

  /**
   * Платіжки ринку тримаються окремим станом, а не в тій самій формі.
   *
   * Вони живуть у join-таблиці, тобто зберігаються іншою процедурою й іншим запитом; змішати
   * їх з полями пресета означало б, що одна невдала половина відкочує другу — або, гірше, не
   * відкочує, і оператор бачить «збережено» на половину змін.
   */
  const allPaymentMethods = useQuery(
    trpc.reference.paymentMethods.list.queryOptions({
      pagination: { limit: 200, direction: 'asc' },
    }),
  );
  const geoPayments = useQuery({
    ...trpc.geo.paymentMethods.list.queryOptions({ geoId: row?.id ?? '' }),
    enabled: row !== undefined,
  });
  const [paymentsDraft, setPaymentsDraft] = useState<string[] | null>(null);
  const savedPayments = (geoPayments.data ?? []).map((entry) => entry.paymentMethodId);
  const selectedPayments = paymentsDraft ?? savedPayments;
  const paymentsDirty = paymentsDraft !== null && paymentsDraft.join(',') !== savedPayments.join(',');
  const setSelectedPayments = (value: string[]): void => {
    setPaymentsDraft(value);
  };
  const savePayments = useMutation(
    trpc.geo.paymentMethods.set.mutationOptions({
      onSuccess: async () => {
        setPaymentsDraft(null);
        await queryClient.invalidateQueries({ queryKey: trpc.geo.pathKey() });
      },
    }),
  );

  // Довідники для «регулятор», «ліцензії в футері» й «хелплайн» — ті самі списки, що на
  // екрані довідників, а не копія в межах ГЕО.
  const licenses = useQuery(
    trpc.reference.licenses.list.queryOptions({ pagination: { limit: 200, direction: 'asc' } }),
  );
  const rgOrgs = useQuery(
    trpc.reference.rgOrganizations.list.queryOptions({ pagination: { limit: 200, direction: 'asc' } }),
  );
  const licenseOptions = (licenses.data?.items ?? []).map((l: { id: string; name: string }) => ({
    value: l.id,
    label: l.name,
  }));
  const rgOptions = (rgOrgs.data?.items ?? []).map((rg: { id: string; name: string }) => ({
    value: rg.id,
    label: rg.name,
  }));

  const refresh = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: trpc.geo.pathKey() });
  };

  const update = useMutation(trpc.geo.presets.update.mutationOptions({ onSuccess: refresh }));
  const create = useMutation(
    trpc.geo.presets.create.mutationOptions({
      onSuccess: async () => {
        await refresh();
        // Панель — рядок реєстру, який щойно з'явився: закриває її лише успіх мутації,
        // так само, як `CreatePanel` закривався не по кліку, а по відповіді сервера.
        onClose();
      },
    }),
  );
  const remove = useMutation(
    trpc.geo.presets.delete.mutationOptions({
      onSuccess: async () => {
        await refresh();
        onClose();
      },
    }),
  );

  const form = useForm<GeoPresetFormValues>({
    /*
     * Явно, хоч це й типове значення: підказка «обмеження на бонуси» нижче читає `form.values`
     * прямо в рендері, і перехід форми в `uncontrolled` мовчки зробив би її мертвою — значення
     * там лишаються застарілими між ререндерами.
     *
     * Тут стояла інша причина, і вона була хибною: нібито `uncontrolled` — типовий режим
     * Mantine 9. Перевірено на встановленому пакеті (`@mantine/form@9.5.1`,
     * `use-form.mjs:18`): типове значення — `controlled`. Підказку насправді вбивав спред
     * `getInputProps`, що стояв ПІСЛЯ явного `error` і затирав його своїм `undefined`.
     */
    mode: 'controlled',
    initialValues: {
      countryCode: row?.countryCode ?? '',
      countryName: source?.countryName ?? '',
      lang1: source?.languages[0] ?? '',
      lang2: source?.languages[1] ?? '',
      lang3: source?.languages[2] ?? '',
      primaryLanguage: source?.primaryLanguage ?? '',
      textDirection: source?.textDirection ?? 'ltr',
      timezone: source?.timezone ?? 'UTC',
      phoneCode: source?.phoneCode ?? '',
      dateFormat: source?.dateFormat ?? 'DD/MM/YYYY',
      numberFormat: source?.numberFormat ?? '1 000,00',
      currencyCode: source?.currencyCode ?? '',
      currencyFormat: source?.currencyFormat ?? '{amount} {currency}',
      minWithdrawal: source?.minWithdrawal == null ? '' : String(source.minWithdrawal / 100),
      maxWithdrawal: source?.maxWithdrawal == null ? '' : String(source.maxWithdrawal / 100),
      payoutTime: source?.payoutTime ?? '',
      regulatorId: source?.regulatorId ?? '',
      footerLicenses: source?.allowedFooterLicenses ?? [],
      minAge: source?.minAge ?? 18,
      kycRequirements: source?.kycRequirements ?? '',
      mandatoryDisclaimer: source?.mandatoryDisclaimer ?? '',
      helplineRef: source?.helplineRef ?? '',
      bonusRestrictions: source?.bonusRestrictions ?? '',
      forbiddenMechanics: source?.forbiddenMechanics ?? [],
      hideBonusBlocks: source?.hideBonusBlocks ?? false,
    },
    validate: zodResolver(geoPresetFormSchema),
  });

  // `exactOptionalPropertyTypes` не дозволяє передати `onDelete: undefined` явно — прибрати
  // видалення на створенні можна лише не передавши проп узагалі, тож він тут окремим об'єктом.
  const deleteProps =
    row === undefined
      ? {}
      : {
          deletePrompt: `Видалити ГЕО «${row.countryName}» назавжди? Разом із ним зникне його словник.`,
          onDelete: () => {
            remove.mutate({ id: row.id });
          },
        };

  return (
    <RowEditor
      title={row === undefined ? 'Новий ГЕО-пресет' : `${row.countryCode} · ${row.countryName}`}
      testId="geo-preset"
      zone="geo_dictionaries"
      onClose={onClose}
      busy={isCreate ? create.isPending : update.isPending}
      error={isCreate ? create.error : update.error}
      deleting={remove.isPending}
      deleteError={remove.error}
      confirmingDelete={confirming}
      onConfirmingDeleteChange={setConfirming}
      {...deleteProps}
      onSubmit={form.onSubmit((values) => {
        // Set знімає повтори, а порожні слоти 2/3 фільтруються — «fr, FR, —» лишається «fr».
        const languages = [...new Set(
          [values.lang1, values.lang2, values.lang3]
            .map((l) => l.trim().toLowerCase())
            .filter(Boolean),
        )];

        // Секції 2–4 мокапа — той самий склад незалежно від режиму, тож поле не можна
        // забути на одному з двох шляхів (`create`/`update`) — воно тут лише одне.
        const detail = {
          textDirection: values.textDirection,
          timezone: values.timezone,
          // Порожнє поле означає «прибрати», а не «лишити» — так само, як дисклеймер
          // і обмеження бонусів нижче.
          phoneCode: values.phoneCode.trim() || null,
          dateFormat: values.dateFormat,
          numberFormat: values.numberFormat,
          currencyFormat: values.currencyFormat,
          // Мажорні одиниці у формі, мінорні — у payload (та сама формула, що бонуси бренду).
          minWithdrawal:
            values.minWithdrawal.trim() === '' ? null : Math.round(Number(values.minWithdrawal) * 100),
          maxWithdrawal:
            values.maxWithdrawal.trim() === '' ? null : Math.round(Number(values.maxWithdrawal) * 100),
          payoutTime: values.payoutTime.trim() || null,
          regulatorId: values.regulatorId || null,
          allowedFooterLicenses: values.footerLicenses,
          kycRequirements: values.kycRequirements.trim() || null,
          mandatoryDisclaimer: values.mandatoryDisclaimer.trim() || null,
          helplineRef: values.helplineRef || null,
          bonusRestrictions: values.bonusRestrictions.trim() || null,
          forbiddenMechanics: values.forbiddenMechanics,
        };

        if (row === undefined) {
          create.mutate({
            countryCode: values.countryCode.trim().toUpperCase(),
            countryName: values.countryName,
            languages,
            primaryLanguage: values.primaryLanguage.trim().toLowerCase(),
            currencyCode: values.currencyCode.toUpperCase(),
            minAge: values.minAge,
            hideBonusBlocks: values.hideBonusBlocks,
            ...detail,
          });
        } else {
          update.mutate({
            id: row.id,
            patch: {
              countryName: values.countryName,
              languages,
              primaryLanguage: values.primaryLanguage.trim().toLowerCase(),
              currencyCode: values.currencyCode.toUpperCase(),
              minAge: values.minAge,
              hideBonusBlocks: values.hideBonusBlocks,
              ...detail,
            },
          });
        }
      })}
    >
      <TextInput
        size="xs"
        label="Код країни"
        placeholder="FR"
        // Ключ, за яким на пресет посилаються сайти й ключові слова — редагується лише до
        // першого збереження. Після створення зміна означає «інша країна», а не «правка поля».
        disabled={!isCreate}
        data-testid="edit-geo-preset-country-code"
        style={{ width: 80 }}
        {...form.getInputProps('countryCode')}
      />
      <TextInput
        size="xs"
        label="Країна"
        data-testid="edit-geo-preset-country"
        style={{ width: 160 }}
        {...form.getInputProps('countryName')}
      />
      <TextInput
        size="xs"
        label="Мова 1"
        data-testid="edit-geo-preset-lang-1"
        style={{ width: 70 }}
        {...form.getInputProps('lang1')}
      />
      <TextInput
        size="xs"
        label="Мова 2"
        placeholder="—"
        data-testid="edit-geo-preset-lang-2"
        style={{ width: 70 }}
        {...form.getInputProps('lang2')}
      />
      {/* Слот навмисно порожній за замовчуванням — мокап малює третю мову саме так. */}
      <TextInput
        size="xs"
        label="Мова 3"
        placeholder="—"
        data-testid="edit-geo-preset-lang-3"
        style={{ width: 70 }}
        {...form.getInputProps('lang3')}
      />
      <TextInput
        size="xs"
        label="Основна"
        data-testid="edit-geo-preset-primary"
        style={{ width: 90 }}
        {...form.getInputProps('primaryLanguage')}
      />
      <Select
        size="xs"
        label="Напрямок тексту"
        data={directionOptions}
        allowDeselect={false}
        data-testid="edit-geo-preset-direction"
        style={{ width: 100 }}
        {...form.getInputProps('textDirection')}
      />
      <TextInput
        size="xs"
        label="Часовий пояс"
        data-testid="edit-geo-preset-timezone"
        style={{ width: 140 }}
        {...form.getInputProps('timezone')}
      />
      <TextInput
        size="xs"
        label="Тел. код"
        placeholder="+34"
        data-testid="edit-geo-preset-phone-code"
        style={{ width: 80 }}
        {...form.getInputProps('phoneCode')}
      />
      <TextInput
        size="xs"
        label="Формат дати"
        data-testid="edit-geo-preset-date-format"
        style={{ width: 120 }}
        {...form.getInputProps('dateFormat')}
      />
      <TextInput
        size="xs"
        label="Формат чисел"
        data-testid="edit-geo-preset-number-format"
        style={{ width: 110 }}
        {...form.getInputProps('numberFormat')}
      />

      <TextInput
        size="xs"
        label="Валюта"
        data-testid="edit-geo-preset-currency"
        style={{ width: 90 }}
        {...form.getInputProps('currencyCode')}
      />
      <TextInput
        size="xs"
        label="Формат суми"
        placeholder="{amount} €"
        data-testid="edit-geo-preset-currency-format"
        style={{ width: 120 }}
        {...form.getInputProps('currencyFormat')}
      />
      <TextInput
        size="xs"
        label="Мін. виведення"
        placeholder="20"
        data-testid="edit-geo-preset-min-withdrawal"
        style={{ width: 100 }}
        {...form.getInputProps('minWithdrawal')}
      />
      <TextInput
        size="xs"
        label="Макс. виведення"
        placeholder="5000"
        data-testid="edit-geo-preset-max-withdrawal"
        style={{ width: 110 }}
        {...form.getInputProps('maxWithdrawal')}
      />
      <TextInput
        size="xs"
        label="Час виплат"
        placeholder="0–72 год"
        data-testid="edit-geo-preset-payout-time"
        style={{ width: 110 }}
        {...form.getInputProps('payoutTime')}
      />

      <Select
        size="xs"
        label="Регулятор"
        placeholder={licenses.isPending ? 'завантаження…' : 'не обрано'}
        data={licenseOptions}
        clearable
        searchable
        data-testid="edit-geo-preset-regulator"
        style={{ width: 180 }}
        {...form.getInputProps('regulatorId')}
      />
      <MultiSelect
        size="xs"
        label="Ліцензії в футері"
        placeholder={licenses.isPending ? 'завантаження…' : undefined}
        data={licenseOptions}
        searchable
        data-testid="edit-geo-preset-footer-licenses"
        style={{ width: 220 }}
        {...form.getInputProps('footerLicenses')}
      />
      <NumberInput
        size="xs"
        label="Вік"
        min={0}
        max={99}
        data-testid="edit-geo-preset-min-age"
        style={{ width: 80 }}
        {...form.getInputProps('minAge')}
      />
      <TextInput
        size="xs"
        label="KYC вимоги"
        data-testid="edit-geo-preset-kyc"
        style={{ width: 180 }}
        {...form.getInputProps('kycRequirements')}
      />
      <TextInput
        size="xs"
        label="Обовʼязковий дисклеймер"
        data-testid="edit-geo-preset-disclaimer"
        style={{ width: 260 }}
        {...form.getInputProps('mandatoryDisclaimer')}
      />
      <Select
        size="xs"
        label="Хелплайн"
        placeholder={rgOrgs.isPending ? 'завантаження…' : 'не обрано'}
        data={rgOptions}
        clearable
        searchable
        data-testid="edit-geo-preset-helpline"
        style={{ width: 200 }}
        {...form.getInputProps('helplineRef')}
      />
      <TextInput
        size="xs"
        label="Обмеження бонусів"
        data-testid="edit-geo-preset-bonus-restrictions"
        style={{ width: 240 }}
        {...form.getInputProps('bonusRestrictions')}
        // `error` — ПІСЛЯ спреду: `getInputProps` теж повертає `error` (з `form.errors`,
        // зазвичай `undefined` до першого сабміту), і спред пізніше в JSX завжди перемагає —
        // раніше явний `error=` тут мовчки затирався тим самим ключем зі спреду.
        //
        // Мокап малює це поле в кольорі помилки, коли перемикач чи заборонена механіка
        // ввімкнені, а поле порожнє — той самий стан, який сервер відхилить при збереженні.
        error={
          (form.values.hideBonusBlocks || form.values.forbiddenMechanics.length > 0) &&
          form.values.bonusRestrictions.trim() === ''
            ? 'Порожнім сервер не збереже — приховані бонуси чи заборонена механіка мусять мати пояснення'
            : form.errors.bonusRestrictions
        }
      />
      <TagsInput
        size="xs"
        label="Заборонені механіки"
        data={[]}
        data-testid="edit-geo-preset-forbidden-mechanics"
        style={{ width: 220 }}
        {...form.getInputProps('forbiddenMechanics')}
      />
      {/* Перемикач, що ховає бонус-блоки в усій мережі країни — ES-style заборона. */}
      <Switch
        size="xs"
        mt={22}
        label="Ховати бонус-блоки"
        data-testid="edit-geo-preset-hide-bonuses"
        {...form.getInputProps('hideBonusBlocks', { type: 'checkbox' })}
      />

      {/*
        * Платіжки ринку — лише в режимі редагування: звʼязка потребує вже створеного `geoId`.
        *
        * `resolveGeoForBuild` рахує саме ці рядки, і записати їх не міг ніхто: CRUD не існував
        * ніде, тож у кожного заведеного через CRM ринку там був нуль, а збірка резолвилась
        * «успішно» з порожнім блоком оплат — і помітити це можна було лише на живій сторінці.
        */}
      {row === undefined ? null : (
        <MultiSelect
          size="xs"
          label="Платіжні методи ринку"
          description="порядок вибору — порядок на сторінці"
          data={(allPaymentMethods.data?.items ?? []).map((method) => ({
            value: method.id,
            label: method.name,
          }))}
          value={selectedPayments}
          onChange={setSelectedPayments}
          searchable
          clearable
          data-testid="edit-geo-preset-payments"
          style={{ width: 460 }}
        />
      )}
      {row === undefined ? null : (
        <Button
          size="xs"
          mt={22}
          loading={savePayments.isPending}
          disabled={!paymentsDirty}
          data-testid="edit-geo-preset-payments-save"
          onClick={() => {
            savePayments.mutate({ geoId: row.id, paymentMethodIds: selectedPayments });
          }}
        >
          Зберегти платіжки
        </Button>
      )}
      {savePayments.error === null ? null : (
        <Text className={classes.danger} data-testid="edit-geo-preset-payments-error" role="alert">
          {publicMessageOf(savePayments.error)}
        </Text>
      )}
    </RowEditor>
  );
}
